import React, { useState } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Grid,
  Box,
  Typography,
  Slider
} from '@mui/material';

// Shape definitions with SVG previews
const SHAPES = [
  { key: 'rectangle', label: 'Rectangle', preview: (color, border) => (
    <svg width="60" height="40" viewBox="0 0 60 40">
      <rect x="2" y="2" width="56" height="36" fill={color} stroke={border} strokeWidth="2" />
    </svg>
  )},
  { key: 'rounded-rect', label: 'Rounded Rectangle', preview: (color, border) => (
    <svg width="60" height="40" viewBox="0 0 60 40">
      <rect x="2" y="2" width="56" height="36" rx="8" fill={color} stroke={border} strokeWidth="2" />
    </svg>
  )},
  { key: 'circle', label: 'Circle', preview: (color, border) => (
    <svg width="60" height="60" viewBox="0 0 60 60">
      <circle cx="30" cy="30" r="27" fill={color} stroke={border} strokeWidth="2" />
    </svg>
  )},
  { key: 'ellipse', label: 'Ellipse', preview: (color, border) => (
    <svg width="60" height="40" viewBox="0 0 60 40">
      <ellipse cx="30" cy="20" rx="27" ry="17" fill={color} stroke={border} strokeWidth="2" />
    </svg>
  )},
  { key: 'triangle', label: 'Triangle', preview: (color, border) => (
    <svg width="60" height="52" viewBox="0 0 60 52">
      <polygon points="30,2 58,50 2,50" fill={color} stroke={border} strokeWidth="2" />
    </svg>
  )},
  { key: 'diamond', label: 'Diamond', preview: (color, border) => (
    <svg width="60" height="60" viewBox="0 0 60 60">
      <polygon points="30,2 58,30 30,58 2,30" fill={color} stroke={border} strokeWidth="2" />
    </svg>
  )},
  { key: 'pentagon', label: 'Pentagon', preview: (color, border) => (
    <svg width="60" height="58" viewBox="0 0 60 58">
      <polygon points="30,2 58,22 48,56 12,56 2,22" fill={color} stroke={border} strokeWidth="2" />
    </svg>
  )},
  { key: 'hexagon', label: 'Hexagon', preview: (color, border) => (
    <svg width="60" height="52" viewBox="0 0 60 52">
      <polygon points="15,2 45,2 58,26 45,50 15,50 2,26" fill={color} stroke={border} strokeWidth="2" />
    </svg>
  )},
  { key: 'star', label: 'Star', preview: (color, border) => (
    <svg width="60" height="58" viewBox="0 0 60 58">
      <polygon points="30,2 36,22 58,22 40,36 48,56 30,44 12,56 20,36 2,22 24,22" fill={color} stroke={border} strokeWidth="2" />
    </svg>
  )},
  { key: 'arrow-right', label: 'Arrow Right', preview: (color, border) => (
    <svg width="60" height="40" viewBox="0 0 60 40">
      <polygon points="2,12 35,12 35,2 58,20 35,38 35,28 2,28" fill={color} stroke={border} strokeWidth="2" />
    </svg>
  )},
  { key: 'arrow-left', label: 'Arrow Left', preview: (color, border) => (
    <svg width="60" height="40" viewBox="0 0 60 40">
      <polygon points="58,12 25,12 25,2 2,20 25,38 25,28 58,28" fill={color} stroke={border} strokeWidth="2" />
    </svg>
  )},
  { key: 'callout', label: 'Callout', preview: (color, border) => (
    <svg width="60" height="50" viewBox="0 0 60 50">
      <path d="M2,2 L58,2 L58,35 L20,35 L10,48 L15,35 L2,35 Z" fill={color} stroke={border} strokeWidth="2" />
    </svg>
  )},
  { key: 'line-horizontal', label: 'Horiz. Line', preview: (color, border) => (
    <svg width="60" height="30" viewBox="0 0 60 30">
      <line x1="4" y1="15" x2="56" y2="15" stroke={border} strokeWidth="3" strokeLinecap="round" />
    </svg>
  )},
  { key: 'line-vertical', label: 'Vert. Line', preview: (color, border) => (
    <svg width="30" height="60" viewBox="0 0 30 60">
      <line x1="15" y1="4" x2="15" y2="56" stroke={border} strokeWidth="3" strokeLinecap="round" />
    </svg>
  )},
  { key: 'line-diagonal-down', label: 'Diag. ↘', preview: (color, border) => (
    <svg width="60" height="50" viewBox="0 0 60 50">
      <line x1="4" y1="4" x2="56" y2="46" stroke={border} strokeWidth="3" strokeLinecap="round" />
    </svg>
  )},
  { key: 'line-diagonal-up', label: 'Diag. ↗', preview: (color, border) => (
    <svg width="60" height="50" viewBox="0 0 60 50">
      <line x1="4" y1="46" x2="56" y2="4" stroke={border} strokeWidth="3" strokeLinecap="round" />
    </svg>
  )},
];

// Predefined colors
const FILL_COLORS = [
  { value: '#FFFFFF', label: 'White' },
  { value: '#F5F5F5', label: 'Light Gray' },
  { value: '#E0E0E0', label: 'Gray' },
  { value: '#FFEB3B', label: 'Yellow' },
  { value: '#FFC107', label: 'Amber' },
  { value: '#FF9800', label: 'Orange' },
  { value: '#F44336', label: 'Red' },
  { value: '#E91E63', label: 'Pink' },
  { value: '#9C27B0', label: 'Purple' },
  { value: '#673AB7', label: 'Deep Purple' },
  { value: '#3F51B5', label: 'Indigo' },
  { value: '#2196F3', label: 'Blue' },
  { value: '#03A9F4', label: 'Light Blue' },
  { value: '#00BCD4', label: 'Cyan' },
  { value: '#009688', label: 'Teal' },
  { value: '#4CAF50', label: 'Green' },
  { value: '#8BC34A', label: 'Light Green' },
  { value: '#CDDC39', label: 'Lime' },
  { value: 'transparent', label: 'None' },
];

const BORDER_COLORS = [
  { value: '#000000', label: 'Black' },
  { value: '#424242', label: 'Dark Gray' },
  { value: '#757575', label: 'Gray' },
  { value: '#D4AF37', label: 'Gold' },
  { value: '#1976D2', label: 'Blue' },
  { value: '#388E3C', label: 'Green' },
  { value: '#D32F2F', label: 'Red' },
  { value: '#7B1FA2', label: 'Purple' },
  { value: 'transparent', label: 'None' },
];

export default function ShapePicker({ open, onClose, onInsert }) {
  const [selectedShape, setSelectedShape] = useState('rectangle');
  const [fillColor, setFillColor] = useState('#FFFFFF');
  const [borderColor, setBorderColor] = useState('#000000');
  const [borderWidth, setBorderWidth] = useState(2);
  const [shapeWidth, setShapeWidth] = useState(150);
  const [shapeHeight, setShapeHeight] = useState(100);

  const handleInsert = () => {
    onInsert({
      type: selectedShape,
      fillColor,
      borderColor,
      borderWidth,
      width: shapeWidth,
      height: shapeHeight,
    });
    onClose();
  };

  const handleReset = () => {
    setSelectedShape('rectangle');
    setFillColor('#FFFFFF');
    setBorderColor('#000000');
    setBorderWidth(2);
    setShapeWidth(150);
    setShapeHeight(100);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ borderBottom: '1px solid #e0e0e0' }}>Insert Shape</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {/* Shape Selection */}
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Select Shape
        </Typography>
        <Grid container spacing={1} sx={{ mb: 3 }}>
          {SHAPES.map(shape => (
            <Grid item xs={4} sm={3} md={2} key={shape.key}>
              <Button
                variant={selectedShape === shape.key ? 'contained' : 'outlined'}
                onClick={() => setSelectedShape(shape.key)}
                sx={{
                  width: '100%',
                  height: 80,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  p: 1,
                  bgcolor: selectedShape === shape.key ? '#5A52A3' : 'transparent',
                  borderColor: selectedShape === shape.key ? '#5A52A3' : '#ccc',
                  '&:hover': {
                    bgcolor: selectedShape === shape.key ? '#4A3F8C' : 'rgba(90, 82, 163, 0.08)',
                    borderColor: '#5A52A3',
                  }
                }}
              >
                {shape.preview(fillColor, borderColor)}
                <Typography variant="caption" sx={{ 
                  fontSize: '0.65rem', 
                  color: selectedShape === shape.key ? '#fff' : 'inherit',
                  lineHeight: 1
                }}>
                  {shape.label}
                </Typography>
              </Button>
            </Grid>
          ))}
        </Grid>

        {/* Fill Color */}
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Fill Color
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 3 }}>
          {FILL_COLORS.map(color => (
            <Box
              key={color.value}
              onClick={() => setFillColor(color.value)}
              sx={{
                width: 28,
                height: 28,
                borderRadius: '4px',
                bgcolor: color.value === 'transparent' ? '#fff' : color.value,
                border: fillColor === color.value ? '3px solid #5A52A3' : '1px solid #ccc',
                cursor: 'pointer',
                position: 'relative',
                '&:hover': { transform: 'scale(1.1)' },
                ...(color.value === 'transparent' && {
                  background: 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)',
                  backgroundSize: '8px 8px',
                  backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px',
                })
              }}
              title={color.label}
            />
          ))}
        </Box>

        {/* Border Color */}
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Border Color
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
          {BORDER_COLORS.map(color => (
            <Box
              key={color.value}
              onClick={() => setBorderColor(color.value)}
              sx={{
                width: 28,
                height: 28,
                borderRadius: '4px',
                bgcolor: color.value === 'transparent' ? '#fff' : color.value,
                border: borderColor === color.value ? '3px solid #5A52A3' : '1px solid #ccc',
                cursor: 'pointer',
                '&:hover': { transform: 'scale(1.1)' },
                ...(color.value === 'transparent' && {
                  background: 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)',
                  backgroundSize: '8px 8px',
                  backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px',
                })
              }}
              title={color.label}
            />
          ))}
        </Box>

        {/* Border Width */}
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Border Width: {borderWidth}px
        </Typography>
        <Slider
          value={borderWidth}
          onChange={(e, val) => setBorderWidth(val)}
          min={0}
          max={10}
          step={1}
          sx={{ width: 200, mb: 3, color: '#5A52A3' }}
        />

        {/* Size */}
        <Box sx={{ display: 'flex', gap: 4 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Width: {shapeWidth}px
            </Typography>
            <Slider
              value={shapeWidth}
              onChange={(e, val) => setShapeWidth(val)}
              min={50}
              max={400}
              step={10}
              sx={{ color: '#5A52A3' }}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Height: {shapeHeight}px
            </Typography>
            <Slider
              value={shapeHeight}
              onChange={(e, val) => setShapeHeight(val)}
              min={50}
              max={400}
              step={10}
              sx={{ color: '#5A52A3' }}
            />
          </Box>
        </Box>

        {/* Preview */}
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
          Preview
        </Typography>
        <Box sx={{ 
          p: 2, 
          bgcolor: '#f5f5f5', 
          borderRadius: 1, 
          display: 'flex', 
          justifyContent: 'center',
          minHeight: 120
        }}>
          {SHAPES.find(s => s.key === selectedShape)?.preview(fillColor, borderColor)}
        </Box>
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid #e0e0e0', p: 2 }}>
        <Button onClick={handleReset} color="inherit">Reset</Button>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleInsert} 
          variant="contained"
          sx={{ 
            bgcolor: '#D4AF37', 
            color: '#1A1A1A',
            '&:hover': { bgcolor: '#F5D76E' }
          }}
        >
          Insert Shape
        </Button>
      </DialogActions>
    </Dialog>
  );
}
